import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { mediaUrl } from "@/lib/api";
import { Printer, ArrowLeft } from "lucide-react";

const fmtDate = (iso) => {
  if (!iso) return "";
  return new Intl.DateTimeFormat("fr-FR", { day: "numeric", month: "long", year: "numeric" }).format(new Date(iso));
};

const validUntil = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  d.setFullYear(d.getFullYear() + 1);
  return fmtDate(d.toISOString());
};

export default function GiftCardPrint() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [content, setContent] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "Carte cadeau — Terre à Deux";
    Promise.all([api.get("/admin/gift-orders"), api.get("/content")])
      .then(([g, c]) => {
        const found = g.data.find((o) => o.id === id);
        if (!found) setError("Commande introuvable");
        setOrder(found);
        setContent(c.data);
      })
      .catch(() => setError("Connectez-vous d'abord à l'espace pro (/admin) pour imprimer une carte."));
  }, [id]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#FBF8F3] flex items-center justify-center p-6">
        <div className="text-center">
          <p data-testid="print-error" className="text-[#A84623] mb-6">{error}</p>
          <a href="/admin" className="text-sm text-[#C85A32] underline">Retour à l'espace pro</a>
        </div>
      </div>
    );
  }

  if (!order || !content) {
    return <div className="min-h-screen bg-[#FBF8F3] flex items-center justify-center text-[#8A7971]">Chargement…</div>;
  }

  const isAbo = order.kind === "abonnement";

  return (
    <div data-testid="gift-card-print-page" className="min-h-screen bg-[#F4EFEA] py-10 px-4 print:bg-white print:py-0 print:px-0">
      <div className="max-w-3xl mx-auto mb-6 flex items-center justify-between print:hidden">
        <a href="/admin" data-testid="print-back-link" className="inline-flex items-center gap-2 text-sm text-[#8A7971] hover:text-[#C85A32] transition-colors">
          <ArrowLeft size={15} /> Retour à l'espace pro
        </a>
        <button
          data-testid="print-btn"
          onClick={() => window.print()}
          className="inline-flex items-center gap-2 rounded-full bg-[#C85A32] px-6 py-3 text-sm font-medium text-white hover:bg-[#A84623] transition-colors"
        >
          <Printer size={15} /> Imprimer la carte
        </button>
      </div>

      <div data-testid="gift-card" className="max-w-3xl mx-auto bg-[#FBF8F3] rounded-3xl border-[3px] border-[#C85A32] overflow-hidden shadow-[0_30px_60px_-25px_rgba(32,24,21,0.3)] print:shadow-none print:rounded-none">
        <div className="border border-dashed border-[#C85A32]/40 m-3 rounded-2xl p-8 md:p-12 print:rounded-none">
          <div className="flex items-center justify-between mb-10">
            {content.logo_url ? (
              <img src={mediaUrl(content.logo_url)} alt={content.brand_name} data-testid="gift-card-logo" className="h-16 w-16 rounded-xl object-cover" />
            ) : (
              <span className="h-16 w-16 rounded-xl bg-[#C85A32] text-[#FBF8F3] flex items-center justify-center font-display italic text-3xl">T</span>
            )}
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#8A7971]">{isAbo ? "Abonnement" : "Carte cadeau"}</p>
              <p className="font-mono-brand text-xs text-[#C85A32] mt-1">{order.code}</p>
            </div>
          </div>

          <p className="font-display italic text-2xl text-[#C85A32] mb-2">{content.brand_name || "Terre à Deux"}</p>
          <h1 className="font-display text-4xl md:text-5xl text-[#201815] leading-tight mb-2">{order.option}</h1>
          <p data-testid="gift-card-value" className="font-mono-brand text-3xl text-[#201815] mb-10">{order.value}</p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#8A7971] mb-1">Pour</p>
              <p className="font-display text-xl text-[#201815]">{order.recipient_name || "………………………"}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#8A7971] mb-1">De la part de</p>
              <p className="font-display text-xl text-[#201815]">{order.buyer_name}</p>
            </div>
          </div>

          {order.message && (
            <p className="font-display italic text-lg text-[#5C4E48] border-l-2 border-[#C85A32]/40 pl-4 mb-8">« {order.message} »</p>
          )}

          <div className="border-t border-dashed border-[#C85A32]/30 pt-6 flex flex-wrap items-end justify-between gap-4">
            <div className="text-xs text-[#8A7971] leading-relaxed">
              <p>{content.address || "25 rue Dessoles, 32000 Auch"}</p>
              <p>{content.phone || "06 31 82 89 64"} — {content.email_contact || "terreadeux@gmail.com"}</p>
              <p className="mt-2">Émise le {fmtDate(order.created_at)} — valable jusqu'au {validUntil(order.created_at)}</p>
            </div>
            <p className="font-display italic text-[#C85A32]">Café céramique — Auch</p>
          </div>
        </div>
      </div>

      <p className="max-w-3xl mx-auto mt-4 text-center text-xs text-[#8A7971] print:hidden">
        Conseil : imprimez en format paysage, puis pliez ou découpez la carte.
      </p>
    </div>
  );
}
